import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { broadcastAuthEvent, AUTH_EVENT_EMAIL_VERIFIED } from '../lib/authBroadcast';

/**
 * /verify-email — landing page users hit from the confirmation link
 * emailed on signup. Extracts `?token=…`, POSTs it to
 * /api/auth/verify-email, and renders one of three states:
 *
 *   • verifying — spinner while the request is in flight
 *   • success   — welcome message + auto-redirect to home in 3s
 *   • error     — clear explanation + a "resend verification" affordance
 *
 * On success the backend also sets the auth cookie, so the user is
 * effectively logged in by the time we render the confirmation.
 */
const VerifyEmail = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, resendVerification } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [errorCode, setErrorCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  useEffect(() => {
    const token = (params.get('token') || '').trim();
    if (!token) {
      setStatus('error');
      setErrorCode('token_missing');
      setErrorMessage('This link is missing its verification token.');
      return;
    }
    (async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
        // Notify a sibling "check your inbox" tab (opened when the
        // user completed signup) that verification just succeeded —
        // that tab listens on the same BroadcastChannel and will
        // auto-advance to /home so the user isn't left with two
        // tabs to reconcile. Safe no-op when BroadcastChannel is
        // unsupported (very old browsers) — those users fall back
        // to AuthModal's polling.
        broadcastAuthEvent(AUTH_EVENT_EMAIL_VERIFIED, { email_hint: null });
        // Auto-redirect to home after a beat so the user gets a moment
        // to read the confirmation before landing on the app.
        setTimeout(() => navigate('/', { replace: true }), 2500);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setStatus('error');
        setErrorCode(detail?.error_code || 'unknown');
        setErrorMessage(detail?.message || 'We could not verify your email. Try requesting a new link.');
      }
    })();
  }, []);

  const handleResend = async () => {
    if (!resendEmail.trim()) return;
    setResending(true);
    setResendMsg('');
    try {
      await resendVerification(resendEmail.trim());
      setResendMsg('If that email exists in our system, a fresh verification link is on its way.');
    } catch (err) {
      setResendMsg('Something went wrong. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12" data-testid="verify-email-page">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-lg p-8 text-center space-y-4">
        {status === 'verifying' && (
          <>
            <Loader2 className="mx-auto text-amber-500 animate-spin" size={40} />
            <h1 className="text-xl font-semibold">Verifying your email…</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">One moment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-500" size={48} />
            <h1 className="text-2xl font-semibold">You&apos;re verified</h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Welcome to Pray Now Together. You&apos;ll be redirected to the home page shortly.
            </p>
            {/* If the user signed up in another tab and clicked the
                email link from their mail client, that original tab
                is already being auto-advanced via BroadcastChannel.
                Let them close this one guilt-free. */}
            <p
              className="text-xs text-zinc-500 dark:text-zinc-500 pt-1"
              data-testid="verify-email-close-hint"
            >
              You can safely close this tab.
            </p>
            <Link
              to="/"
              className="inline-block mt-2 h-11 px-6 leading-[44px] rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors"
              data-testid="verify-email-continue-btn"
            >
              Continue now
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="mx-auto text-rose-500" size={48} />
            <h1 className="text-xl font-semibold">
              {errorCode === 'token_expired' ? 'This link expired' : "We couldn't verify your email"}
            </h1>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">{errorMessage}</p>
            <div className="pt-4 space-y-2 text-left">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Resend verification link
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="verify-email-resend-input"
                    className="w-full h-11 pl-9 pr-3 rounded-xl border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm outline-none focus:border-amber-500 dark:focus:border-amber-400"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={!resendEmail.trim() || resending}
                  data-testid="verify-email-resend-btn"
                  className="h-11 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
                >
                  {resending ? '…' : 'Send'}
                </button>
              </div>
              {resendMsg && (
                <p className="text-xs text-zinc-600 dark:text-zinc-400" data-testid="verify-email-resend-status">
                  {resendMsg}
                </p>
              )}
            </div>
            <Link
              to="/"
              className="inline-block mt-4 text-sm text-amber-600 dark:text-amber-400 hover:underline"
              data-testid="verify-email-home-link"
            >
              Back to home
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
